/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Key Management & Telemetry Studio View (Task 10.6.1 & Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Key,
  Plus,
  ShieldCheck,
  Zap,
  CheckCircle2,
  Ban,
  Lock,
  ExternalLink,
  Activity,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import { useApiKeyStore } from '../stores/useApiKeyStore';
import { ApiKeyListTable } from '../components/apikeys/ApiKeyListTable';
import { CreateApiKeyModal } from '../components/apikeys/CreateApiKeyModal';
import { SecretKeyRevealModal } from '../components/apikeys/SecretKeyRevealModal';
import { RevokeKeyModal } from '../components/apikeys/RevokeKeyModal';
import { DeveloperAnalyticsDashboard } from '../components/apikeys/DeveloperAnalyticsDashboard';

export const ApiKeyManagementView: React.FC = () => {
  const { t } = useTranslation();
  const { getStats, setCreateModalOpen } = useApiKeyStore();
  const [activeTab, setActiveTab] = useState<'keys' | 'analytics'>('keys');

  const stats = getStats();

  return (
    <div className="space-y-6 pb-20">
      {/* Studio Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <Key className="size-5" />
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground">
              {t('apikeys.title')}
            </h1>
          </div>
          <p className="text-xs text-foreground-muted max-w-2xl">
            {t('apikeys.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover shadow-sm hover:shadow-md transition-all active:scale-95"
          >
            <Plus className="size-4" />
            <span>{t('apikeys.createNewKey')}</span>
          </button>
        </div>
      </div>

      {/* Studio Navigation Tabs (API Keys vs Analytics & Telemetry) */}
      <div className="flex items-center justify-between border-b border-surface-border pb-px">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('keys')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-all ${
              activeTab === 'keys'
                ? 'border-primary text-primary'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            <Key className="size-4" />
            <span>{t('apikeys.tabKeys')}</span>
          </button>

          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-all ${
              activeTab === 'analytics'
                ? 'border-primary text-primary'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            <Activity className="size-4" />
            <span>{t('apikeys.tabAnalytics')}</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-primary/10 text-primary">
              Live
            </span>
          </button>
        </div>
      </div>

      {/* Tab 1: API Keys Management */}
      {activeTab === 'keys' && (
        <div className="space-y-6">
          {/* Metrics Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Keys */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-foreground-muted font-medium">
                  {t('apikeys.statsTotalKeys')}
                </span>
                <p className="text-2xl font-bold font-heading text-foreground">
                  {stats.total}
                </p>
              </div>
              <div className="size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Key className="size-5" />
              </div>
            </div>

            {/* Active Keys */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-foreground-muted font-medium">
                  {t('apikeys.statsActiveKeys')}
                </span>
                <p className="text-2xl font-bold font-heading text-emerald-500">
                  {stats.active}
                </p>
              </div>
              <div className="size-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="size-5" />
              </div>
            </div>

            {/* Revoked Keys */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-foreground-muted font-medium">
                  {t('apikeys.statsRevokedKeys')}
                </span>
                <p className="text-2xl font-bold font-heading text-rose-500">
                  {stats.revoked}
                </p>
              </div>
              <div className="size-10 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center">
                <Ban className="size-5" />
              </div>
            </div>

            {/* Total RPM Capacity */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-foreground-muted font-medium">
                  {t('apikeys.statsTotalRpm')}
                </span>
                <p className="text-2xl font-bold font-heading text-primary">
                  {stats.totalRpm} <span className="text-xs font-normal text-foreground-muted">RPM</span>
                </p>
              </div>
              <div className="size-10 rounded-xl bg-accent/15 text-accent flex items-center justify-center">
                <Zap className="size-5" />
              </div>
            </div>
          </div>

          {/* Main API Keys List Table */}
          <ApiKeyListTable />

          {/* Security Architecture Callout */}
          <div className="p-4 rounded-2xl border border-surface-border bg-surface/60 backdrop-blur-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="size-9 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                <ShieldCheck className="size-5" />
              </div>
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-foreground">
                    Cloudflare Edge SHA-256 Hash Guard
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                    <Lock className="size-3" />
                    Zero Plaintext Storage
                  </span>
                </div>
                <p className="text-[11px] text-foreground-muted">
                  Plaintext secret keys are revealed only once at creation time. The API Gateway authenticates requests at edge nodes via SHA-256 hash lookup and cached KV tokens with sub-millisecond latency.
                </p>
              </div>
            </div>

            <a
              href="/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border hover:border-primary text-xs font-medium text-foreground-muted hover:text-foreground transition-colors shrink-0"
            >
              <span>OpenAPI Reference</span>
              <ExternalLink className="size-3.5" />
            </a>
          </div>
        </div>
      )}

      {/* Tab 2: Analytics & Telemetry Dashboard */}
      {activeTab === 'analytics' && (
        <DeveloperAnalyticsDashboard />
      )}

      {/* Modals */}
      <CreateApiKeyModal />
      <SecretKeyRevealModal />
      <RevokeKeyModal />
    </div>
  );
};
